// Group SCOPE — the one place that answers "does this schema field belong to a
// quote line, or to the quote as a whole?"
//
// The FormSchema is per-line by construction: SchemaForm mounts once per
// QuoteLine and lineConfigIssues (form-validation.ts) iterates every field for
// every line. Adding a quote-level question as a plain schema field therefore
// renders it N times and flags EVERY line as missing it — at the Save report,
// the client price pre-flight and the backend price gate simultaneously, because
// ADR-011 deliberately wired all three to one validator. `FormFieldGroup.scope`
// is what separates the two grains; this module is the single reader of it.
//
// A field with no group is `line` — ungrouped fields render in the leading
// section of the per-line form, which is exactly what they did before scope
// existed. Defaulting the other way would silently move them off every line.
//
// See docs/ADR-018 Decision 5.
export const DEFAULT_FORM_FIELD_GROUP_SCOPE = 'line';
// group_key → scope, for the fields of one schema. Built once per caller rather
// than re-scanned per field (lineConfigIssues runs this over every line).
export function groupScopeByKey(schema) {
    const byKey = new Map();
    for (const g of schema.groups)
        byKey.set(g.key, g.scope);
    return byKey;
}
export function fieldScope(field, scopeByGroupKey) {
    if (field.group_key === null)
        return DEFAULT_FORM_FIELD_GROUP_SCOPE;
    return scopeByGroupKey.get(field.group_key) ?? DEFAULT_FORM_FIELD_GROUP_SCOPE;
}
// The schema narrowed to ONE scope — same shape, so every existing consumer
// (renderer, scrubber, default-filler, upload-slot deriver, validator) works
// unchanged on the narrowed value instead of needing its own scope branch.
//
// Returns the SAME object when nothing is filtered out, so a memo keyed on the
// result stays referentially stable for the overwhelmingly common case of a
// schema whose every group is line-scoped.
export function scopeSchema(schema, scope) {
    const scopeByGroupKey = groupScopeByKey(schema);
    const groups = schema.groups.filter((g) => g.scope === scope);
    const fields = schema.fields.filter((f) => fieldScope(f, scopeByGroupKey) === scope);
    if (groups.length === schema.groups.length && fields.length === schema.fields.length) {
        return schema;
    }
    return { ...schema, groups, fields };
}
//# sourceMappingURL=form-scope.js.map